/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ComponentProps,
    forwardRef,
    memo,
    useState,
    useCallback,
    useMemo,
    useEffect,
    lazy,
    Suspense,
} from 'react';
import { Link } from '@/components/link';

import type { ShopperSearch } from '@/scapi';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';

import { cn } from '@/lib/utils';
import {
    createProductUrl,
    getDecoratedVariationAttributes,
    getCollectionName,
    type DecoratedVariationAttributeValue,
} from '@/lib/product/product-utils';
import { getProductRating } from '@/lib/product/product-utils-plp';
import { isDesktopViewport, useProductTileContext } from './context';
import { DeferredWishlistButton } from './deferred-wishlist-button';
import { useWishlistLoader } from '@/providers/wishlist';
import { PickupIcon } from '@/components/icons';
import { QuickAddButton } from './quick-add-button';
import { ProductTileSwatchesSkeleton } from '@/components/category-skeleton';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import { ProductImageContainer } from '@/components/product-image';
import ProductPrice from '@/components/product-price';
import { StarRating } from '@/components/product-ratings/star-rating';
import { UITarget } from '@/targets/ui-target';
import { Card } from '@/components/ui/card';
import { loader as loaders } from './loaders';

const LazySwatches = lazy(() => import('./swatches').then((m) => ({ default: m.ProductTileSwatches })));

const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID = 'color';
const PRODUCT_TILE_MAX_SWATCHES = 3;

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('productTile', {
    name: 'Product Tile',
    description: 'Configurable product tile with customizable styling for images, typography, and hover effects',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class ProductTileMetadata {
    @AttributeDefinition({
        id: 'productId',
        name: 'Product',
        description: 'Select a product to render in this tile.',
        type: 'product',
    })
    productId?: string;

    @AttributeDefinition({
        id: 'objectFit',
        name: 'Image Object Fit',
        description: 'How the product image should fit within its container',
        type: 'enum',
        values: ['contain', 'cover', 'fill', 'none', 'scale-down'],
        defaultValue: 'contain',
    })
    objectFit?: string;

    @AttributeDefinition({
        id: 'borderRadius',
        name: 'Border Radius',
        description: 'Corner roundness of the tile card',
        type: 'enum',
        values: ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'full'],
        defaultValue: 'xl',
    })
    borderRadius?: string;

    @AttributeDefinition({
        id: 'boxShadow',
        name: 'Box Shadow',
        description: 'Shadow effect for the tile card',
        type: 'enum',
        values: ['none', 'sm', 'md', 'lg', 'xl', '2xl'],
        defaultValue: 'sm',
    })
    boxShadow?: string;

    @AttributeDefinition({
        id: 'padding',
        name: 'Padding',
        description: 'Padding on all sides of the tile',
        type: 'enum',
        values: ['0', '2', '4', '6', '8'],
        defaultValue: '0',
    })
    padding?: string;

    @AttributeDefinition({
        id: 'margin',
        name: 'Margin',
        description: 'Margin on all sides of the tile',
        type: 'enum',
        values: ['0', '2', '4', '6', '8'],
        defaultValue: '0',
    })
    margin?: string;

    @AttributeDefinition({
        id: 'fontWeight',
        name: 'Font Weight',
        description: 'Weight of the product name text',
        type: 'enum',
        values: ['normal', 'medium', 'semibold', 'bold'],
        defaultValue: 'semibold',
    })
    fontWeight?: string;

    @AttributeDefinition({
        id: 'letterSpacing',
        name: 'Letter Spacing',
        description: 'Spacing between letters in product name',
        type: 'enum',
        values: ['tighter', 'tight', 'normal', 'wide', 'wider'],
        defaultValue: 'normal',
    })
    letterSpacing?: string;

    @AttributeDefinition({
        id: 'hoverEffect',
        name: 'Hover Effect',
        description: 'Interactive hover effect for the tile',
        type: 'enum',
        values: ['default', 'scale', 'shadow', 'lift'],
        defaultValue: 'default',
    })
    hoverEffect?: string;
}
/* v8 ignore stop */

const getPageDesignerStyleClasses = ({
    objectFit,
    borderRadius,
    boxShadow,
    padding,
    margin,
    fontWeight,
    letterSpacing,
    hoverEffect,
}: Partial<ProductTileProps>) => {
    const classes: string[] = [];

    if (objectFit) {
        const fitMap = {
            contain: '[&_img]:!object-contain',
            cover: '[&_img]:!object-cover',
            fill: '[&_img]:!object-fill',
            none: '[&_img]:!object-none',
            'scale-down': '[&_img]:!object-scale-down',
        };
        classes.push(fitMap[objectFit]);
    }

    if (borderRadius) {
        const radiusMap: Record<string, string> = {
            none: '!rounded-none',
            xs: '!rounded-xs',
            sm: '!rounded-sm',
            md: '!rounded-md',
            lg: '!rounded-lg',
            xl: '!rounded-xl',
            '2xl': '!rounded-2xl',
            '3xl': '!rounded-3xl',
            '4xl': '!rounded-4xl',
            full: '!rounded-full',
        };
        classes.push(radiusMap[borderRadius] || '!rounded-none');
    }

    if (boxShadow === 'none') {
        classes.push('!shadow-none hover:!shadow-none');
    } else if (boxShadow) {
        const shadowMap = {
            sm: '!shadow-sm hover:!shadow-sm',
            md: '!shadow-md hover:!shadow-md',
            lg: '!shadow-lg hover:!shadow-lg',
            xl: '!shadow-xl hover:!shadow-xl',
            '2xl': '!shadow-2xl hover:!shadow-2xl',
        };
        classes.push(shadowMap[boxShadow]);
    }

    if (padding && padding !== '0') {
        classes.push(`p-${padding}`);
    }

    if (margin && margin !== '0') {
        classes.push(`m-${margin}`);
    }

    if (fontWeight) {
        const weightMap = {
            normal: '[&_a]:!font-normal',
            medium: '[&_a]:!font-medium',
            semibold: '[&_a]:!font-semibold',
            bold: '[&_a]:!font-bold',
        };
        classes.push(weightMap[fontWeight]);
    }

    if (letterSpacing) {
        const spacingMap = {
            tighter: '[&_a]:!tracking-tighter',
            tight: '[&_a]:!tracking-tight',
            normal: '[&_a]:!tracking-normal',
            wide: '[&_a]:!tracking-wide',
            wider: '[&_a]:!tracking-wider',
        };
        classes.push(spacingMap[letterSpacing]);
    }

    if (hoverEffect && hoverEffect !== 'default') {
        const hoverMap = {
            scale: 'hover:!scale-105 !transition-transform !duration-200 hover:!shadow-md',
            shadow: 'hover:!shadow-xl !transition-shadow !duration-200',
            lift: 'hover:!-translate-y-1 hover:!shadow-lg !transition-all !duration-200',
        };
        classes.push(hoverMap[hoverEffect]);
    }

    return classes.join(' ');
};

export interface ProductTileProps extends ComponentProps<'div'> {
    product?: ShopperSearch.schemas['ProductSearchHit'];
    productId?: string;
    maxSwatches?: number;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** For variant products, filter swatches to only show this variant's color value */
    selectedVariantColorValue?: string | null;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show the pickup available indicator icon with tooltip */
    showPickupAvailable?: boolean;
    /** Custom quick add button label */
    quickAddLabel?: string;
    /** Top-level navigation category name shown below swatches (e.g. "Men", "Women") */
    topCategoryName?: string;
    /** Accepted for API compatibility; has no effect */
    showNavigationArrows?: boolean;

    // Page Designer styling props
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    borderRadius?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
    boxShadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    padding?: '0' | '2' | '4' | '6' | '8';
    margin?: '0' | '2' | '4' | '6' | '8';
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    letterSpacing?: 'tighter' | 'tight' | 'normal' | 'wide' | 'wider';
    hoverEffect?: 'default' | 'scale' | 'shadow' | 'lift';

    // Page Designer system props (need to be filtered)
    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

const ProductTile = memo(
    forwardRef<HTMLDivElement, ProductTileProps>(
        (
            {
                className,
                product: productProp,
                productId: _productId,
                maxSwatches = PRODUCT_TILE_MAX_SWATCHES,
                selectedVariantColorValue,
                handleProductClick,
                imgAspectRatio,
                showPickupAvailable = false,
                quickAddLabel,
                // RGBARRY-CUSTOMIZATION: top-category label removed from tile; prop kept for API compatibility
                topCategoryName: _topCategoryName,
                showNavigationArrows: _showNavigationArrows,
                // Page Designer styling props
                objectFit,
                borderRadius,
                boxShadow,
                padding,
                margin,
                fontWeight,
                letterSpacing,
                hoverEffect,
                // Page Designer system props (filter out)
                regionId: _regionId,
                component: _component,
                componentData: _componentData,
                designMetadata: _designMetadata,
                data,
                ...props
            },
            ref
        ) => {
            // Prioritize loader data (Page Designer) over prop (programmatic use)
            const product = (data as ShopperSearch.schemas['ProductSearchHit'] | undefined) || productProp;

            const {
                config,
                t,
                currency,
                getBadges,
                resolvedProductPaths,
                enableRepresentedPrices,
                representedPrices,
                requestRepresentedPrice,
            } = useProductTileContext();

            // RGBARRY-CUSTOMIZATION (W-23875749): the resolver's true canonical path for this product, when the
            // PLP loader batch-resolved it and streamed it into the provider. Preferred over the local slugify
            // derivation so tile links are byte-identical to production; `undefined` falls back to slugify.
            const resolvedProductPath = product?.productId ? resolvedProductPaths[product.productId] : undefined;

            const productData = useMemo(() => {
                if (!product) return null;
                return {
                    badges: getBadges(product),
                    rating: getProductRating(product),
                    // RGBARRY-CUSTOMIZATION: merchandising collection name (c_collection) shown as a
                    // minor label between the product name and price on the tile.
                    collectionName: getCollectionName(product),
                };
            }, [product, getBadges]);

            const effectiveImgAspectRatio =
                imgAspectRatio ?? config.global.productListing.defaultProductTileImgAspectRatio;

            // RGBARRY-CUSTOMIZATION: detect masters via productType.master (+ representedProduct), not
            // product.variants. PLP search hits omit variants[] since we dropped allVariationProperties
            // to fix the PLP timeout, so the old `!!product.variants` check made every master look like
            // a standard product and dropped the ?pid= from the tile link — clicks then landed on a bare
            // master with no colour/variant selected. representedProduct.id survives the `variations`
            // expand, so it still yields the default variant to link to.
            const isMasterProd = !!product?.variants || !!product?.productType?.master;
            const isBundleOrSet = product?.productType?.bundle || product?.productType?.set;
            const representedVariant = isMasterProd
                ? product?.variants?.find((variant) => variant?.productId === product?.representedProduct?.id)
                : undefined;

            // RGBARRY-CUSTOMIZATION: merchant-chosen default variant. `c_defaultVariantColor` holds a
            // variant SKU (see the add-product-defaultvariantcolor migration); the int_urlresolver_scapi
            // product_search response hook (productSearchHooks.js) resolves that SKU's colour into
            // `c_defaultVariantColorValue` server-side, on the hit itself, before the response is cached.
            // When set, the tile shows that variant's image and links (and prices) to exactly that
            // variant. Empty -> today's behaviour (the represented / search-elected variant). The colour
            // drives the image below; the SKU drives the pid here.
            const merchantDefaultVariantSku =
                isMasterProd && !isBundleOrSet
                    ? (product as { c_defaultVariantColor?: string } | undefined)?.c_defaultVariantColor?.trim() ||
                      undefined
                    : undefined;
            const merchantDefaultVariantColor =
                (product as { c_defaultVariantColorValue?: string } | undefined)?.c_defaultVariantColorValue ||
                undefined;

            // Prefer the merchant's default-variant SKU; fall back to the represented variant (today's).
            // This one id drives the tile link, the represented-price lookup, and — via its resolved
            // colour — the image, so all three stay in sync on the pictured variant.
            const defaultVariantPid =
                isMasterProd && !isBundleOrSet
                    ? (merchantDefaultVariantSku ?? product?.representedProduct?.id ?? null)
                    : null;

            // RGBARRY-CUSTOMIZATION: register this master's represented (face-out) variant for the grid's batched
            // price lookup. The search hit's `price` is the master's minimum across variants, which can differ
            // wildly from the pictured variant's real price; the provider fetches the represented ids after first
            // paint (see resource.represented-prices.ts) and the price cell below swaps in the resolved value.
            // No-op unless the surrounding grid opted in via `enableRepresentedPrices`.
            useEffect(() => {
                if (enableRepresentedPrices && defaultVariantPid) {
                    requestRepresentedPrice(defaultVariantPid);
                }
            }, [enableRepresentedPrices, defaultVariantPid, requestRepresentedPrice]);

            // RGBARRY-CUSTOMIZATION: colour-story PLPs slot the *variant* (not the master) into the
            // category, so the search hit is productType.variant with representedProduct = the variant
            // itself and a single-value colour variationAttribute (e.g. only "Jam"). Unlike master hits
            // there's no represented colour to pre-select, so selectedAttributeValue stays null and the
            // image falls back to the first imageGroup — often a different colour (e.g. "Black") — while
            // the swatch correctly reads "Jam". Seeding the selected colour from the variant's own colour
            // attribute realigns image + swatch. Master hits are untouched (isVariantProd is false).
            const isVariantProd = !!product?.productType?.variant;
            const variantColorValue =
                isVariantProd && PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID === 'color'
                    ? (product?.variationAttributes?.find((attr) => attr.id === PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID)
                          ?.values?.[0]?.value ?? undefined)
                    : undefined;

            // use the representedVariant values to get a product for PDP
            // RGBARRY-CUSTOMIZATION: fall back to representedProduct.c_color when variants[] is absent.
            // PLP search hits omit variants[] (we dropped allVariationProperties to fix the PLP timeout),
            // so representedVariant is undefined and the tile image would default to the master's own
            // default colour — which can differ from the represented (default) variant. That made the
            // tile show one colour (e.g. red) while the click landed on the represented variant's PDP
            // (e.g. black). The colour variation values are the c_color strings themselves, so seeding
            // selectedAttributeValue from representedProduct.c_color aligns the tile image with the
            // linked variant. Degrades safely: an unmatched colour falls back to the default image.
            const initialVariationValue =
                selectedVariantColorValue !== undefined && selectedVariantColorValue !== null
                    ? selectedVariantColorValue
                    : // RGBARRY-CUSTOMIZATION: the merchant default-variant colour (resolved from
                      // `c_defaultVariantColor`) wins over the represented/first-image fallbacks so the tile
                      // image matches the SKU the click lands on. Undefined when the attribute is unset or the
                      // colour could not be resolved (e.g. prod-only SKU on a local sandbox) -> today's chain.
                      (merchantDefaultVariantColor ??
                      representedVariant?.variationValues?.[PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID] ??
                      variantColorValue ??
                      (isMasterProd && PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID === 'color'
                          ? ((product?.representedProduct as { c_color?: string } | undefined)?.c_color ?? undefined)
                          : undefined));

            // Local swatch selection state — drives image switching and selected ring on swatches.
            // Initialized from the URL-driven prop; updates when the user clicks a swatch on the tile.
            const [selectedAttributeValue, setSelectedAttributeValue] = useState<string | null>(
                initialVariationValue || null
            );

            useEffect(() => {
                if (selectedVariantColorValue !== undefined && selectedVariantColorValue !== null) {
                    setSelectedAttributeValue(selectedVariantColorValue);
                }
            }, [selectedVariantColorValue]);

            // Pre-seed every quick-add swatch from the tile's represented variant, with the
            // locally-selected color overriding the represented variant's color when set.
            const initialVariantSelections = useMemo<Record<string, string> | undefined>(() => {
                const representedVariantSelections: Record<string, string> = {
                    ...(representedVariant?.variationValues ?? {}),
                };
                if (selectedAttributeValue) {
                    representedVariantSelections[PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID] = selectedAttributeValue;
                }
                return Object.keys(representedVariantSelections).length > 0 ? representedVariantSelections : undefined;
            }, [representedVariant, selectedAttributeValue]);

            const variationAttributes = useMemo(
                () =>
                    product ? getDecoratedVariationAttributes(product, { canonicalBasePath: resolvedProductPath }) : [],
                [product, resolvedProductPath]
            );
            const colorAttributes = variationAttributes.filter(({ id }) => PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID === id);
            const colorValues = (colorAttributes[0]?.values?.slice(0, maxSwatches) ??
                []) as DecoratedVariationAttributeValue[];

            const handleSwatchHover = useCallback(
                (value: string) => {
                    // Read the viewport at hover time rather than subscribing during render: swatch hover is desktop-only,
                    // and a render-time media query would mismatch SSR and force a post-hydration re-render of every tile.
                    if (isDesktopViewport()) {
                        setSelectedAttributeValue(value);
                    }
                },
                [setSelectedAttributeValue]
            );
            const handleClick = useCallback(() => {
                product && handleProductClick?.(product);
            }, [handleProductClick, product]);

            // Gate the lazy wishlist load on first hover/focus/touch of the whole tile, not the
            // heart icon. The heart is `opacity-0 group-hover:opacity-100` — revealed by hovering
            // the tile — so binding the trigger to the heart alone means it never fires until the
            // pointer reaches the heart's box, leaving the revealed heart empty. Idempotent.
            const loadWishlist = useWishlistLoader();
            const handleTileIntent = useCallback(() => {
                void loadWishlist();
            }, [loadWishlist]);

            const productName = product?.productName ?? '';
            const productUrl = createProductUrl(
                product?.productId ?? '',
                null,
                'color',
                defaultVariantPid,
                productName,
                resolvedProductPath
            );

            const pageDesignerStyles = getPageDesignerStyleClasses({
                objectFit,
                borderRadius,
                boxShadow,
                padding,
                margin,
                fontWeight,
                letterSpacing,
                hoverEffect,
            });

            if (!product) {
                return (
                    <Card
                        ref={ref}
                        className={cn(
                            'product-card group w-full min-w-0 max-w-full overflow-hidden gap-0 py-0',
                            pageDesignerStyles,
                            className
                        )}
                        {...props}>
                        <div className="p-4 text-sm text-muted-foreground">{t('selectProduct')}</div>
                    </Card>
                );
            }

            return (
                <Card
                    ref={ref}
                    className={cn(
                        // RGBARRY-CUSTOMIZATION: white card, no shadow (baggallini tile styling)
                        'product-card group w-full min-w-0 max-w-full cursor-pointer overflow-hidden gap-0 py-0 bg-background [--ui-shadow:none]',
                        pageDesignerStyles,
                        className
                    )}
                    onPointerEnter={handleTileIntent}
                    onFocus={handleTileIntent}
                    onTouchStart={handleTileIntent}
                    {...props}>
                    {/* Image area */}
                    {/* RGBARRY-CUSTOMIZATION: light-gray image backdrop */}
                    <div className="product-image relative bg-[#F4F4F4]">
                        {/* RGBARRY-CUSTOMIZATION: named group scopes the image rollover swap (in
                            product-image/index.tsx) to just this image zone. The Card above also
                            declares a bare `group` (for the quick-add button reveal below) — the
                            image's hover-swap must not fire from hovering the swatches/name/price
                            in the info section, only the image (and its full-image click overlay
                            Link just below, which stacks on top of and would otherwise swallow the
                            hover if the group lived one level down, inside the image itself). */}
                        <div className="relative w-full overflow-hidden group/imgswap">
                            <ProductImageContainer
                                product={product}
                                selectedColorValue={
                                    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID === 'color' ? selectedAttributeValue : null
                                }
                                imgAspectRatio={effectiveImgAspectRatio}
                                // RGBARRY-CUSTOMIZATION: 3:4 contained product image on multiply-blended backdrop
                                className="w-full aspect-[3/4] [&_img]:object-contain! [&_img]:h-full! [&_img]:max-w-full! [&_img]:mx-auto! [&_img]:mix-blend-multiply!"
                                handleProductClick={handleProductClick}
                            />
                            <UITarget targetId="sfcc.plp.shipping.deliveryEstimate" />

                            {/* Clickable product link overlay — mouse convenience only, hidden from AT */}
                            <Link
                                to={productUrl}
                                className="absolute inset-0 z-[1] cursor-pointer"
                                aria-hidden="true"
                                onClick={handleClick}
                                tabIndex={-1}
                            />

                            {/* Badges — top-left */}
                            {productData?.badges.hasBadges && (
                                <div className="absolute top-2 left-2 flex flex-col items-start gap-1 z-20">
                                    {productData.badges.badges.map((badge) => (
                                        <span
                                            key={badge.label}
                                            data-slot="badge"
                                            className="px-2 py-1 text-xs font-semibold uppercase inline-block bg-foreground text-background leading-none rounded-ui">
                                            {badge.label}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Action icons — top-right */}
                            <div className="absolute top-2 right-2 flex flex-col items-end gap-2 z-20">
                                {showPickupAvailable && (
                                    <div
                                        className="group/pickup relative"
                                        role="img"
                                        aria-label={t('pickupAvailable')}
                                        data-testid="pickup-available-indicator">
                                        <div className="w-9 h-9 p-2 bg-muted text-muted-foreground flex items-center justify-center">
                                            <PickupIcon className="w-4 h-4" aria-hidden="true" />
                                        </div>
                                        <div className="absolute right-0 top-full mt-1 z-50 opacity-0 group-hover/pickup:opacity-100 transition-opacity duration-200 pointer-events-none">
                                            <div className="bg-foreground text-background text-xs font-medium px-2 py-1 whitespace-nowrap shadow-lg">
                                                {t('pickupAvailable')}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* RGBARRY-CUSTOMIZATION: heart always visible (not hover-only) */}
                                <div className="transition-opacity duration-300">
                                    <DeferredWishlistButton
                                        product={product}
                                        surface="plp"
                                        size="sm"
                                        // RGBARRY-CUSTOMIZATION: transparent wishlist button (no chrome)
                                        className="relative top-auto right-auto z-20 !bg-transparent !shadow-none border-0"
                                    />
                                </div>
                            </div>

                            {/* RGBARRY-CUSTOMIZATION: removed hover dark-tint overlay */}

                            {/* Quick Add button */}
                            {/* RGBARRY-CUSTOMIZATION: hover-reveal is a desktop affordance — hide below lg
                                (matches the tile's lg-gated swatch hover) so it never shows on mobile/tablet */}
                            <div className="absolute bottom-4 left-0 right-0 px-4 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100 transition-opacity duration-300 z-20 hidden lg:block">
                                <QuickAddButton
                                    productId={product.productId ?? ''}
                                    productName={productName}
                                    selectedColorValue={selectedAttributeValue}
                                    initialVariantSelections={initialVariantSelections}
                                    label={quickAddLabel ?? t('quickAdd')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Info section */}
                    <div className="relative p-4">
                        {/* Color swatches */}
                        {colorValues.length > 0 && (
                            <div>
                                <Suspense fallback={<ProductTileSwatchesSkeleton count={maxSwatches} />}>
                                    <LazySwatches
                                        colorValues={colorValues}
                                        selectedAttributeValue={selectedAttributeValue}
                                        onSwatchHover={handleSwatchHover}
                                        onSwatchClick={handleClick}
                                        productName={productName}
                                        totalColorCount={colorAttributes[0]?.values?.length ?? colorValues.length}
                                        maxSwatches={maxSwatches}
                                        productHref={productUrl}
                                    />
                                </Suspense>
                            </div>
                        )}

                        {/* RGBARRY-CUSTOMIZATION: removed store name and top-category labels */}

                        {/* Product name — the single keyboard/SR tab stop for this tile */}
                        <h3 className="text-lg font-semibold leading-[120%] tracking-[-0.45px] text-card-foreground mb-2">
                            <Link
                                to={productUrl}
                                className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                onClick={handleClick}>
                                {productName}
                            </Link>
                        </h3>

                        {/* RGBARRY-CUSTOMIZATION: removed SKU line */}

                        {/* RGBARRY-CUSTOMIZATION: collection name (c_collection) — minor label between name and price */}
                        {productData?.collectionName && (
                            <p className="mb-2 text-sm font-normal leading-[120%] text-muted-foreground">
                                {productData.collectionName}
                            </p>
                        )}

                        {/* RGBARRY-CUSTOMIZATION: star rating only when reviews exist; dark stars + count */}
                        {productData && productData.rating.reviewCount > 0 && productData.rating.rating > 0 && (
                            <div className="mb-2 flex items-center gap-1">
                                <StarRating
                                    rating={productData.rating.rating}
                                    reviewCount={productData.rating.reviewCount}
                                    starSize="sm"
                                    starClassName="text-[#1a1a1a]"
                                    showRatingLabel={false}
                                    showRatingLink={false}
                                />
                                <span className="text-xs text-muted-foreground">
                                    ({productData.rating.reviewCount})
                                </span>
                            </div>
                        )}

                        {/* Price */}
                        {(() => {
                            // RGBARRY-CUSTOMIZATION: for masters on a represented-price-enabled grid, show the
                            // *represented* (face-out) variant's price instead of the master's minimum. Render
                            // exactly as today — including the current master price — on first paint; the batched
                            // lookup resolves after paint and we quietly swap in the represented price only once we
                            // have it (a `number`). While it's still loading (`undefined`) or resolved-missing/
                            // failed (`null`), we keep showing today's price. No skeleton, no layout shift.
                            const representedEntry =
                                enableRepresentedPrices && defaultVariantPid
                                    ? representedPrices[defaultVariantPid]
                                    : null;

                            const priceProduct =
                                typeof representedEntry === 'number' && product
                                    ? { ...product, price: representedEntry, priceMax: representedEntry }
                                    : product;

                            return (
                                <div>
                                    <ProductPrice
                                        type="unit"
                                        product={priceProduct}
                                        currency={currency ?? config.commerce.sites?.[0]?.defaultCurrency ?? ''}
                                        labelForA11y={(product?.productName ?? product?.productId) || ''}
                                        // RGBARRY-CUSTOMIZATION: show current price only (no list/strikethrough)
                                        currentPriceOnly
                                        currentPriceProps={{
                                            className:
                                                'text-lg font-semibold leading-[120%] tracking-[-0.45px] text-card-foreground',
                                        }}
                                        listPriceProps={{
                                            className: 'text-muted-foreground text-sm leading-none line-through',
                                        }}
                                        promoCalloutProps={{
                                            className: 'text-xs text-active-foreground mt-1',
                                        }}
                                        className="text-sm"
                                    />
                                </div>
                            );
                        })()}
                        <UITarget targetId="sfcc.productCard.loyalty.points" />
                        <UITarget targetId="sfcc.productCard.bnpl.message" />
                    </div>
                </Card>
            );
        }
    )
);

ProductTile.displayName = 'ProductTile';

// oxlint-disable-next-line react-refresh/only-export-components
export const loader = loaders.server;
export { ProductTile };
// oxlint-disable-next-line react-refresh/only-export-components
export { ProductTileProvider, useProductTileContext } from './context';
export default ProductTile;
